/**
 * Redux thunks for managing MAT (Multi-Academy Trust) related data and reports
 */

/**
 * Imports required for the thunks
 */
import { getMatGroupReport } from '../../../../api/reportAPI';
import { getDataUploadsForChildSchools } from '../../../../api/groupsAPI';
import { KeyStages } from '../../../../utils/subscriptionTypes';
import {
	editReportBundleLabelAndBookmarkedStatus,
	activateLatestReportsRequest,
	getGroupsReportsByGroupAccessId,
	notifyGroupReportReady,
	areNewReportsForGroupAvailable,
} from '../../../../api/groupReportsAPI';
import {
	setChildPending,
	setChildSuccess,
	setChildFail,
	setDataUploadsSuccess,
	setDataUploadsFail,
	setGroupsReportsByGroupAccessIdFailed,
	setGroupsReportsByGroupAccessIdSuccess,
	setGroupsReportsByGroupAccessIdPending,
	setEditReportBundleLabelAndBookmarkedStatusFailed,
	setGenerateReportRequestPending,
	setGenerateReportRequestFailed,
	setGenerateReportRequestSuccess,
	setShowReportInfo,
	setReportAccordionLoading,
	setHasNewGroupReports,
	setHasNewGroupReportsError,
} from '.';
import { translate } from '../../../../utils/locale';
import { getEmbargo, getViewAsSchool } from '../../../app/redux/context';
import { getFeature } from 'features/app/redux/features';

/**
 * Thunk to fetch MAT group report data
 * @returns {AppThunk} A thunk that dispatches actions to handle the MAT group report request lifecycle
 */
export const childThunk = (): AppThunk => async (dispatch) => {
	dispatch(setChildPending());

	getMatGroupReport()
		.then((response) => {
			dispatch(setChildSuccess(response));
		})
		.catch(() => {
			dispatch(
				setChildFail({
					text: 'Please speak to customer services',
				} as MAT.Message)
			);
		});
};

/**
 * Thunk to fetch data uploads for child schools for a specific academic year
 * @param {number} academicYear - The academic year to fetch data uploads for
 * @returns {AppThunk} A thunk that dispatches actions to handle the data uploads request lifecycle
 */
export const getDataUploadsThunk = (academicYear: number): AppThunk => async (dispatch) => {
	getDataUploadsForChildSchools(academicYear)
		.then((response) => {
			const responseObject = response.map((dataUploadResponse: any) => {
				return {
					clientName: dataUploadResponse.clientName,
					dataSharing: dataUploadResponse.dataSharing,
					keyStage4ContractInformation: dataUploadResponse.keyStageContractInformation.find(
						(dataUpload: MAT.KeyStageContractInformation) => dataUpload.keyStage === KeyStages.KS4
					),
					keyStage4PublishInformation: dataUploadResponse.keyStagePublishInformation.find(
						(dataUpload: MAT.KeyStagePublishInformation) => dataUpload.keyStage === KeyStages.KS4
					),
					keyStage5ContractInformation: dataUploadResponse.keyStageContractInformation.find(
						(dataUpload: MAT.KeyStageContractInformation) => dataUpload.keyStage === KeyStages.KS5
					),
					keyStage5PublishInformation: dataUploadResponse.keyStagePublishInformation.find(
						(dataUpload: MAT.KeyStagePublishInformation) => dataUpload.keyStage === KeyStages.KS5
					),
				};
			});

			dispatch(setDataUploadsSuccess(responseObject));
		})
		.catch(() => {
			dispatch(setDataUploadsFail());
		});
};

/**
 * Thunk to fetch groups reports by group access ID
 * @param {number} groupAccessId - The group access ID to fetch groups reports for
 * @returns {AppThunk} A thunk that dispatches actions to handle the groups reports request lifecycle
 * @throws Will dispatch a failure action if the request fails or if the response contains an error
 */
export const fetchGroupsReportsByGroupAccessIdThunk = (): AppThunk => async (
	dispatch,
	getState
) => {
	dispatch(setShowReportInfo(false));

	const state = getState();

	//get the embargo dates
	const embargo = getEmbargo(state);

	//is the embargo feature enabled
	const embargoFeature = getFeature('embargo_feature')(state);

	//is the group within the embargo period
	const isWithinEarlyAccess = embargoFeature && embargo?.some((x) => x.value === true);

	//is the group an LA or MAT
	const hasViewAsPermissions = getViewAsSchool(state);

	try {
		// if it's embargo period, let's save ourselves some API requests and not bother
		if (isWithinEarlyAccess && !hasViewAsPermissions) {
			dispatch(setReportAccordionLoading(false));

			//Moving this down to stop flashing of the accordion loading state - this is only for the embargo period
			dispatch(setGroupsReportsByGroupAccessIdPending());
			return;
		} else {
			// it's not embargo, load activated reports and check for new ones
			// Keep the accordion in a loading state until Summit responds so we don't
			// flash the "no reports available" empty state (e.g. when switching to a
			// previous academic year that still has bundles).
			dispatch(setGroupsReportsByGroupAccessIdPending());

			// load pre-existing - activated reports
			const activatedReportsResponse: ResponseBuilder<
				MAT.ReportBundle[]
			> = await getGroupsReportsByGroupAccessId();
			if (activatedReportsResponse.hasError) {
				dispatch(setGroupsReportsByGroupAccessIdFailed());
			} else {
				const reports = activatedReportsResponse.responseObject;
				dispatch(setGroupsReportsByGroupAccessIdSuccess(reports));
			}

			// see if we have any new reports
			const response: ResponseBuilder<boolean> = await areNewReportsForGroupAvailable();

			if (response.hasError) {
				// display an error stating that there is an error
				// calling this will automatically set has new group reports to false
				dispatch(setHasNewGroupReportsError());
			} else {
				// successful response, let's parse it
				const hasNewGroupReports = response.responseObject;
				dispatch(setHasNewGroupReports(hasNewGroupReports));
			}
		}
	} catch (error) {
		dispatch(setGroupsReportsByGroupAccessIdFailed());
	}
};

/**
 * Thunk to edit the label and bookmarked status of a report bundle
 * @param {number} groupAccessId - The group access ID to edit the report bundle for
 * @param {number} id - The id of the report bundle
 * @param {string} label - The label of the report bundle
 * @param {boolean} isBookmarked - The bookmarked status of the report bundle
 * @returns {AppThunk} A thunk that dispatches actions to handle the edit report bundle request lifecycle
 */
export const editReportBundleLabelAndBookmarkedStatusThunk = (
	id: number,
	label: string,
	isBookmarked: boolean
): AppThunk => async (dispatch) => {
	/**
	 * Try to edit report bundle label and bookmarked status
	 */
	try {
		/**
		 * Edit report bundle label and bookmarked status
		 */
		const response: ResponseBuilder<MAT.ReportBundle> = await editReportBundleLabelAndBookmarkedStatus(
			id,
			label,
			isBookmarked
		);

		/**
		 * If there is an error, set the edit report bundle label and bookmarked status failed
		 */
		if (response.hasError) {
			dispatch(
				setEditReportBundleLabelAndBookmarkedStatusFailed({
					text: translate('summit.groupReports.groupReportsPage.step2.requestingReportError'),
				} as MAT.Message)
			);
		} else {
			/**
			 * If there is no error, fetch the groups reports by group access id
			 */
			dispatch(fetchGroupsReportsByGroupAccessIdThunk());
		}
	} catch (error) {
		/**
		 * If there is an error, set the edit report bundle label and bookmarked status failed
		 */
		dispatch(
			setEditReportBundleLabelAndBookmarkedStatusFailed({
				text: 'Please speak to customer services',
			} as MAT.Message)
		);
	}
};

/**
 * Thunk to dispatch all report requests
 * @returns {AppThunk} A thunk that dispatches actions to handle the dispatch all report requests lifecycle
 */
export const dispatchAllReportRequestsThunk = (): AppThunk => async (dispatch) => {
	// Set the report request pending status to true to show a UI message
	dispatch(setGenerateReportRequestPending(true));

	await dispatch(activateLatestReportRequestThunk());

	//set the report request pending status to false to hide the UI message
	dispatch(setGenerateReportRequestPending(false));
};

/**
 * Thunk to activate the latest reports for all key stages
 * @returns {AppThunk} A thunk that dispatches actions to handle the activate report request lifecycle
 */
export const activateLatestReportRequestThunk = (): AppThunk => async (dispatch) => {
	try {
		const response: ResponseBuilder<MAT.ActivatedBundlesResponse> = await activateLatestReportsRequest();
		/**
		 * If there is an error, set the report request status failed
		 */
		if (response.hasError) {
			dispatch(
				setGenerateReportRequestFailed({
					text: translate('summit.groupReports.groupReportsPage.step2.requestingReportError'),
				} as MAT.Message)
			);
		} else {
			/**
			 * It was successful, trigger signal r notifications and retrieve reports
			 */
			dispatch(setGenerateReportRequestSuccess());

			// Notify all users in the MAT that the new group report is ready
			try {
				// extract the response
				var activatedBundles = response.responseObject;

				// only notify users based on what key stage changed
				await createSignalRNotifications(activatedBundles);
			} catch (notificationError) {
				// Log the notification error but don't fail the whole process
				console.error('Failed to send group report notification:', notificationError);
			}

			/**
			 * If there is no error, fetch the groups reports by group access id
			 */
			dispatch(fetchGroupsReportsByGroupAccessIdThunk());
		}
	} catch (error) {
		dispatch(
			setGenerateReportRequestFailed({
				text: translate('summit.groupReports.groupReportsPage.step2.requestingReportError'),
			} as MAT.Message)
		);
	}
};

/**
 * Function to create the signal r notifications based on the key stage that changed
 */
async function createSignalRNotifications(activatedBundles: MAT.ActivatedBundlesResponse) {
	// if keystage 4 changed, create a notification that those reports are activated
	if (activatedBundles.ks4ReportsActivated) {
		await notifyGroupReportReady('KS4');
	}

	// if keystage 5 changed, create a notification that those reports are activated
	if (activatedBundles.ks5ReportsActivated) {
		await notifyGroupReportReady('KS5');
	}
}
